import React, { useEffect, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import { Button, Group, Paper, Stack, Tooltip, FileInput } from "@mantine/core";
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconList,
  IconListNumbers,
  IconQuote,
  IconLink,
  IconPhoto,
  IconDeviceFloppy,
  IconUpload,
} from "@tabler/icons-react";
import { useUpdateTorSpecification } from "../hooks/useTorDocuments";
import { notifications } from "@mantine/notifications";

interface TorSpecificationEditorProps {
  tenderId: string;
  initialContent?: string;
  onSave?: (content: string) => void;
  readOnly?: boolean;
}

export const TorSpecificationEditor: React.FC<TorSpecificationEditorProps> = ({
  tenderId,
  initialContent = "",
  onSave,
  readOnly = false,
}) => {
  const [content, setContent] = useState(initialContent);
  const fileInputRef = useRef<any>(null);
  const updateSpecification = useUpdateTorSpecification(tenderId);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false, // Disable from StarterKit to use explicit extension
        orderedList: false, // Disable from StarterKit to use explicit extension
        listItem: false, // Disable from StarterKit to use explicit extension
      }),
      BulletList,
      OrderedList,
      ListItem,
      Image,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
    },
  });

  useEffect(() => {
    if (editor && initialContent !== content) {
      editor.commands.setContent(initialContent);
      setContent(initialContent);
    }
  }, [initialContent, editor]);

  const handleSave = async () => {
    if (onSave) {
      onSave(content);
    } else {
      await updateSpecification.mutateAsync({ torSpecification: content });
    }
  };

  const addImage = () => {
    const url = window.prompt("Enter image URL:");
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const handleImageUpload = async (file: File | null) => {
    if (!file || !editor) return;

    try {
      // For now, we'll use a simple approach - convert to base64
      // In a real app, you'd upload to MinIO and get a URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        if (base64) {
          editor.chain().focus().setImage({ src: base64 }).run();
          notifications.show({
            title: 'Success',
            message: 'Image uploaded successfully',
            color: 'green',
          });
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to upload image',
        color: 'red',
      });
    }
  };

  const addLink = () => {
    const url = window.prompt("Enter URL:");
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {!readOnly && (
          <Group gap="xs">
            <Tooltip label="Bold">
              <Button
                variant={editor.isActive("bold") ? "filled" : "light"}
                size="xs"
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <IconBold size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Italic">
              <Button
                variant={editor.isActive("italic") ? "filled" : "light"}
                size="xs"
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <IconItalic size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Underline">
              <Button
                variant={editor.isActive("underline") ? "filled" : "light"}
                size="xs"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
              >
                <IconUnderline size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Bullet List">
              <Button
                variant={editor.isActive("bulletList") ? "filled" : "light"}
                size="xs"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <IconList size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Numbered List">
              <Button
                variant={editor.isActive("orderedList") ? "filled" : "light"}
                size="xs"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <IconListNumbers size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Blockquote">
              <Button
                variant={editor.isActive("blockquote") ? "filled" : "light"}
                size="xs"
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
              >
                <IconQuote size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Add Link">
              <Button variant="light" size="xs" onClick={addLink}>
                <IconLink size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Add Image URL">
              <Button variant="light" size="xs" onClick={addImage}>
                <IconPhoto size={16} />
              </Button>
            </Tooltip>

            <Tooltip label="Upload Image">
              <FileInput
                ref={fileInputRef}
                accept="image/*"
                size="xs"
                style={{ width: 'auto' }}
                onChange={handleImageUpload}
                placeholder="Upload Image"
                leftSection={<IconUpload size={16} />}
              />
            </Tooltip>
          </Group>
        )}

        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            padding: "12px",
            minHeight: "200px",
            backgroundColor: readOnly ? "#f8f9fa" : "white",
          }}
        >
          <EditorContent editor={editor} />
        </div>

        {!readOnly && (
          <Group justify="right">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={updateSpecification.isPending}
              disabled={updateSpecification.isPending}
            >
              Save Specification
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};
